import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { UploadDataDetails } from '../../src/api/api';
import { Codeowners } from '../../src/consts/codeowners';
import { prepareAWSKey } from '../../src/state/utils';
import { captureException } from '../../src/utils/exception';
import { getMimeType } from '../../src/utils/getMimeType';
import { getUploadData } from '../api/api';

type Photo = {
  file: File;
  isLoading: boolean;
  isError: boolean;
  awsLink: string | null;
};

type Props = {
  token?: string;
};

export const useUploadPhoto = ({ token }: Props) => {
  const queryClient = useQueryClient();

  const { data: photos = [] } = useQuery<Photo[]>(['photos'], {
    initialData: [],
  });

  const { mutateAsync: uploadPhotoMutation } = useMutation(
    async (photo: File) => {
      const mimeType = await getMimeType(photo);
      if (!token || !mimeType) {
        throw new Error('Invalid token or mime type');
      }
      const alreadyExists = photos.some((p) => p.file === photo);

      if (!alreadyExists) {
        queryClient.setQueryData<Photo[]>(['photos'], (oldPhotos = []) => [
          ...oldPhotos,
          { file: photo, isLoading: true, isError: false, awsLink: null },
        ]);
      }

      const response = await getUploadData({
        token,
        mimeType: mimeType.mime,
      });

      return { response, photo };
    },
    {
      onSuccess: async ({ response, photo }) => {
        const form = new FormData();
        Object.keys(response.data?.fields || {}).forEach((key) =>
          form.append(key, response.data?.fields[key as keyof UploadDataDetails['fields']] || '')
        );
        form.append('file', photo);

        const awsKey = response.data?.fields.key || null;

        if (!response.data?.url) {
          throw new Error('Upload URL is undefined');
        }
        await fetch(response.data.url, { method: 'POST', body: form })
          .then(() => {
            if (photo.name && awsKey) {
              queryClient.setQueryData<Photo[]>(['photos'], (oldPhotos = []) =>
                oldPhotos.map((p) =>
                  p.file === photo
                    ? { ...p, isLoading: false, isError: false, awsLink: prepareAWSKey(photo.name, awsKey) }
                    : p
                )
              );
            }
          })
          .catch((err) => {
            captureException(err, Codeowners.CUSTOMER_INTAKE);
            queryClient.setQueryData<Photo[]>(['photos'], (oldPhotos = []) =>
              oldPhotos.map((p) => (p.file === photo ? { ...p, isLoading: false, isError: true, awsLink: null } : p))
            );
          });
      },
      onError: (_error, photo) => {
        queryClient.setQueryData<Photo[]>(['photos'], (oldPhotos = []) =>
          oldPhotos.map((p) => (p.file === photo ? { ...p, isLoading: false, isError: true } : p))
        );
      },
    }
  );

  const deletePhoto = (photo: File) => {
    queryClient.setQueryData<Photo[]>(['photos'], (oldPhotos = []) => oldPhotos.filter((p) => p.file !== photo));
  };

  const uploadPhoto = async (photo: File) => {
    await uploadPhotoMutation(photo);
  };

  return { photos, uploadPhoto, deletePhoto };
};
